import React, { useState, useEffect } from "react";
import { db } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  arrayUnion,
  addDoc,
  Timestamp,
} from "firebase/firestore";

import { FaArrowLeft, FaArrowRight } from "react-icons/fa"; // optional icons

const CarsManagement = () => {
  const [cars, setCars] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [investorUID, setInvestorUID] = useState("");
  const [ownershipAmount, setOwnershipAmount] = useState("");
  const [profitAmount, setProfitAmount] = useState(""); // For distributing payouts
  const [loading, setLoading] = useState(false);

  // Dark theme styles
  const pageStyle = {
    backgroundColor: "#1E1E1E",
    color: "#fff",
    minHeight: "100vh",
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  };

  const headingStyle = {
    fontSize: "2rem",
    marginBottom: "20px",
    textAlign: "center",
  };

  const navStyle = {
    marginBottom: "20px",
    textAlign: "center",
  };

  const navListStyle = {
    listStyle: "none",
    display: "inline-flex",
    gap: "20px",
    padding: 0,
    margin: 0,
  };

  const navItemStyle = {
    cursor: "pointer",
  };

  const selectStyle = {
    padding: "6px",
    width: "300px",
    borderRadius: "5px",
    border: "1px solid #555",
    backgroundColor: "#2C2C2C",
    color: "#fff",
  };

  const carContainerStyle = {
    border: "1px solid #444",
    borderRadius: "8px",
    backgroundColor: "#2C2C2C",
    padding: "20px",
    marginTop: "20px",
  };

  const buttonStyle = {
    padding: "8px 16px",
    borderRadius: "5px",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    marginRight: "10px",
  };

  const redButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#f44336",
  };

  const greenButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#4CAF50",
  };

  const blueButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#007bff",
  };

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "cars"));
        const carsData = querySnapshot.docs.map((docu) => ({
          id: docu.id,
          ...docu.data(),
        }));
        setCars(carsData);
      } catch (error) {
        console.error("Error fetching cars:", error);
      }
    };

    fetchCars();
  }, []);

  const handleCarSelect = (carId) => {
    const car = cars.find((c) => c.id === carId);
    setSelectedCarId(carId);
    setSelectedCar(car);
  };

  const handleAddInvestor = async () => {
    if (!selectedCarId || !investorUID || !ownershipAmount) {
      alert("Car, UID, and Ownership Amount are required.");
      return;
    }

    try {
      setLoading(true);

      const ownershipAmountFloat = parseFloat(ownershipAmount);

      // Update the car's investors array and moneyRaised field
      const carRef = doc(db, "cars", selectedCarId);
      await updateDoc(carRef, {
        investors: arrayUnion(investorUID),
        moneyRaised: (selectedCar.moneyRaised || 0) + ownershipAmountFloat,
      });

      // Create an investment document
      const investmentData = {
        userID: investorUID,
        carID: selectedCarId,
        amount: ownershipAmountFloat,
        date: Timestamp.now(),
      };

      const investmentRef = await addDoc(collection(db, "investments"), investmentData);
      await updateDoc(doc(db, "investments", investmentRef.id), {
        investmentID: investmentRef.id,
      });

      alert("Investor added, money raised updated, and investment document created successfully!");
      setInvestorUID("");
      setOwnershipAmount("");

      // Update local state to reflect new moneyRaised
      setSelectedCar((prev) => ({
        ...prev,
        moneyRaised: (prev.moneyRaised || 0) + ownershipAmountFloat,
      }));

      setLoading(false);
    } catch (error) {
      console.error("Error adding investor:", error);
      setLoading(false);
    }
  };

  const distributePayouts = async () => {
    if (!selectedCarId || !profitAmount) {
      alert("Please select a car and enter the profit amount.");
      return;
    }

    try {
      const carInvestors = selectedCar.investors || [];
      const totalValue = selectedCar.totalValue || 0;

      if (totalValue === 0) {
        alert("Total value of the car is zero. Cannot distribute payouts.");
        return;
      }

      for (const uid of carInvestors) {
        // Calculate how much the investor owns in this car
        const investorPaymentsSnapshot = await getDocs(collection(db, "investments"));
        let investorOwnership = 0;

        investorPaymentsSnapshot.forEach((docu) => {
          const data = docu.data();
          if (data.carID === selectedCarId && data.userID === uid) {
            investorOwnership += data.amount || 0;
          }
        });

        const ownershipPercentage = investorOwnership / totalValue;
        const payoutAmount = profitAmount * ownershipPercentage;

        // Create a payout document
        const payoutData = {
          carID: selectedCarId,
          userID: uid,
          amount: payoutAmount,
          date: Timestamp.now(),
        };

        const payoutRef = await addDoc(collection(db, "payouts"), payoutData);
        await updateDoc(doc(db, "payouts", payoutRef.id), {
          payoutID: payoutRef.id,
        });
      }

      alert("Payouts distributed successfully!");
      setProfitAmount("");
    } catch (error) {
      console.error("Error distributing payouts:", error);
    }
  };

  const toggleActiveStatus = async () => {
    if (!selectedCarId) return;
    try {
      const carRef = doc(db, "cars", selectedCarId);
      await updateDoc(carRef, {
        active: !selectedCar.active,
      });
      setSelectedCar((prev) => ({ ...prev, active: !prev.active }));
    } catch (error) {
      console.error("Error toggling active status:", error);
    }
  };

  const toggleVisibility = async () => {
    if (!selectedCarId) return;
    try {
      const carRef = doc(db, "cars", selectedCarId);
      await updateDoc(carRef, {
        private: !selectedCar.private,
      });
      setSelectedCar((prev) => ({ ...prev, private: !prev.private }));
    } catch (error) {
      console.error("Error toggling visibility:", error);
    }
  };

  return (
    <div style={pageStyle}>
      <h1 style={headingStyle}>Cars Management</h1>

      {/* Navigation */}
      <nav style={navStyle}>
        <ul style={navListStyle}>
          <li style={navItemStyle}>
            {/* Make it go to the marketplace instead of add */}
            <a href="/marketplace" style={{ color: "#007bff", textDecoration: "none" }}>
              Add New Car
            </a>
          </li>
          <li style={navItemStyle}>
            <a href="list" style={{ color: "#007bff", textDecoration: "none" }}>
              View All Cars
            </a>
          </li>
        </ul>
      </nav>

      {/* Dropdown to select a car */}
      <div style={{ margin: "20px 0" }}>
        <label htmlFor="carSelect" style={{ marginRight: "10px" }}>
          Select a Car:
        </label>
        <select
          id="carSelect"
          value={selectedCarId || ""}
          onChange={(e) => handleCarSelect(e.target.value)}
          style={Object.assign({}, { color: "#fff", backgroundColor: "#2C2C2C", padding: "5px", borderRadius: "5px", border: "1px solid #555", width: "300px" })}
        >
          <option value="" disabled>
            Choose a car
          </option>
          {cars.map((car) => (
            <option key={car.id} value={car.id}>
              {car.carName || "Unnamed Car"}
            </option>
          ))}
        </select>
      </div>

      {/* Display car details if one is selected */}
      {selectedCar && (
        <div style={carContainerStyle}>
          <h2 style={{ marginTop: 0 }}>{selectedCar.carName || "Unnamed Car"}</h2>
          <p>
            <strong>Car ID:</strong> {selectedCar.id}
          </p>
          <img
            src={selectedCar.pictures?.[0] || "https://via.placeholder.com/400"}
            alt={selectedCar.carName || "Car"}
            style={{
              width: "100%",
              height: "250px",
              objectFit: "cover",
              borderRadius: "8px",
              marginBottom: "15px",
            }}
          />
          <p>
            <strong>Year:</strong> {selectedCar.year || "N/A"}
          </p>
          <p>
            <strong>Total Value:</strong> ${selectedCar.totalValue?.toLocaleString() || "N/A"}
          </p>
          <p>
            <strong>Money Raised:</strong> ${selectedCar.moneyRaised?.toLocaleString() || "N/A"}
          </p>
          <p>
            <strong>Location:</strong> {selectedCar.location || "N/A"}
          </p>
          <p>
            <strong>Status:</strong> {selectedCar.active ? "Active" : "Inactive"}
          </p>
          <button
            onClick={toggleActiveStatus}
            style={selectedCar.active ? redButtonStyle : greenButtonStyle}
          >
            {selectedCar.active ? "Set Inactive" : "Set Active"}
          </button>
          <p>
            <strong>Visibility:</strong> {selectedCar.private ? "Private" : "Public"}
          </p>
          <button
            onClick={toggleVisibility}
            style={selectedCar.private ? redButtonStyle : greenButtonStyle}
          >
            {selectedCar.private ? "Set Public" : "Set Private"}
          </button>

          <h3 style={{ marginTop: "20px" }}>Manage Investors</h3>
          {selectedCar.investors?.length > 0 ? (
            <ul>
              {selectedCar.investors.map((uid) => (
                <li key={uid} style={{ marginBottom: "5px" }}>
                  UID: {uid}
                  <button
                    onClick={() => alert("Remove functionality not implemented yet")}
                    style={{
                      ...redButtonStyle,
                      padding: "4px 8px",
                      marginLeft: "10px",
                      fontSize: "0.8rem",
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p>No investors found.</p>
          )}

          <div style={{ marginTop: "10px" }}>
            <h5 style={{ marginBottom: "10px" }}>Add Investor</h5>
            <input
              type="text"
              placeholder="Investor UID"
              value={investorUID}
              onChange={(e) => setInvestorUID(e.target.value)}
              style={{
                marginRight: "10px",
                padding: "5px",
                width: "300px",
                borderRadius: "5px",
                border: "1px solid #555",
                backgroundColor: "#2C2C2C",
                color: "#fff",
              }}
            />
            <input
              type="number"
              placeholder="Ownership Amount"
              value={ownershipAmount}
              onChange={(e) => setOwnershipAmount(e.target.value)}
              style={{
                marginRight: "10px",
                padding: "5px",
                width: "200px",
                borderRadius: "5px",
                border: "1px solid #555",
                backgroundColor: "#2C2C2C",
                color: "#fff",
              }}
            />
            <button onClick={handleAddInvestor} style={greenButtonStyle}>
              Add Investor
            </button>
          </div>

          {/* Distribute Payouts Section */}
          <div style={{ marginTop: "20px" }}>
            <h5 style={{ marginBottom: "10px" }}>Distribute Payouts</h5>
            <input
              type="number"
              placeholder="Enter profit amount"
              value={profitAmount}
              onChange={(e) => setProfitAmount(e.target.value)}
              style={{
                marginRight: "10px",
                padding: "5px",
                width: "300px",
                borderRadius: "5px",
                border: "1px solid #555",
                backgroundColor: "#2C2C2C",
                color: "#fff",
              }}
            />
            <button onClick={distributePayouts} style={blueButtonStyle}>
              Distribute Payouts
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarsManagement;
